import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { FaComment } from 'react-icons/fa';
import { saveFeedback } from '../../firebase_utils/firebaseUtils';
import { AuthContext } from '../../contexts/AuthContext';

// Custom event name for opening feedback from other components
export const OPEN_FEEDBACK_EVENT = 'open-feedback-modal';

// Simplified Feedback component with theme matching Landing.js and Home.js
const Feedback = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const buttonRef = useRef(null);
  const textareaRef = useRef(null);

  // Set loaded state after component mounts with slight delay for animation
  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 300);
  }, []);

  // Auto-resize textarea as content grows
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    // Reset height to get accurate scrollHeight
    textarea.style.height = 'auto';
    // Set the height to scrollHeight to fit the content
    textarea.style.height = `${Math.max(24 * 4, textarea.scrollHeight)}px`;
  }, [feedbackText]);

  // Listen for custom event to open feedback modal from other components
  useEffect(() => {
    const handleOpenFeedback = () => {
      setIsModalOpen(true);
    };
    
    window.addEventListener(OPEN_FEEDBACK_EVENT, handleOpenFeedback);
    
    return () => {
      window.removeEventListener(OPEN_FEEDBACK_EVENT, handleOpenFeedback);
    };
  }, []);

  // Handle feedback submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const feedbackId = user?.uid || `anonymous_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const feedbackData = {
        rating: 5, // Default to 5 stars
        feedbackType: 'general', // Default type
        feedbackText,
        isAnonymous: !user,
        userEmail: user?.email || 'anonymous',
        timestamp: new Date().toISOString(),
        path: location.pathname
      };

      await saveFeedback(feedbackData, feedbackId);
      setSubmitSuccess(true);
      
      // Reset form after 1.5 seconds
      setTimeout(() => {
        setIsModalOpen(false);
        setFeedbackText('');
        setSubmitSuccess(false);
      }, 1500);
    } catch (error) {
      console.error("Feedback submission error:", error);
      setSubmitError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current path is the landing page
  const isLandingPage = location.pathname === '/';

  return (
    <>
      {/* Floating Feedback Button with matching gradient and animation */}
      <button 
        ref={buttonRef}
        onClick={() => setIsModalOpen(true)}
        className={`fixed ${isLandingPage ? 'bottom-20' : 'bottom-4'} right-4 sm:right-6
                   w-auto h-12 sm:h-14 px-4 sm:px-5 rounded-full shadow-lg 
                   flex items-center justify-center text-white gap-2
                   hover:scale-105 transition-all duration-200
                   focus:outline-none focus:ring-2 focus:ring-offset-2 
                   focus:ring-[#FF5757] z-[9999]
                   transform ${isLoaded ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
                   motion-safe:transition-all motion-safe:duration-300
                   bg-gradient-to-r from-[#FF5757] to-[#FFAA00]
                   pb-[env(safe-area-inset-bottom)]`}
        aria-label="Feedback"
      >
        {/* Star pattern within button similar to Landing page */}
        <div className="absolute inset-0 opacity-10 rounded-full bg-[radial-gradient(white_1px,transparent_1px)] bg-[length:8px_8px]"></div>
        <div className="absolute inset-0 rounded-full bg-[#FF5757] animate-ping opacity-30" style={{ animationDuration: '3s' }}></div>
        <FaComment className="w-5 h-5 sm:w-6 sm:h-6 relative z-10" />
        <span className="font-medium relative z-10 hidden sm:inline">Provide Feedback</span>
      </button>

      {/* Feedback Modal with app theme */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] 
                      flex items-center justify-center p-4 overflow-y-auto backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl 
                        transform transition-all duration-300 ease-out
                        relative overflow-hidden">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-[#FF5757] to-[#FFAA00] p-4">
              <h3 className="text-2xl font-bold text-white font-kidHeading">We'd love your feedback!</h3>
            </div>
            
            {/* Star pattern background */}
            <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#FF5757_1px,transparent_1px)] bg-[length:16px_16px]"></div>
            
            {/* Close button */}
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-white hover:text-gray-200 
                         p-2 rounded-full bg-black/20 hover:bg-black/30
                         z-20 transition-all duration-200"
              aria-label="Close feedback modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="p-6 relative z-10">
              {submitSuccess ? (
                <div className="text-center py-6 text-green-600">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-r from-[#22C55E] to-[#0EA5E9] flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-xl font-semibold font-kidHeading">Thank you for your feedback!</p>
                  <p className="text-gray-600 mt-2">We appreciate you taking the time to help us improve.</p>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="md:w-1/3">
                    <p className="text-gray-600 text-sm">
                      Thank you for taking the time! We read every feedback message.
                      {!user && <span className="text-gray-400 block mt-1">Submitting anonymously.</span>}
                    </p>
                  </div>

                  <div className="md:w-2/3">
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="relative">
                        <textarea
                          ref={textareaRef}
                          value={feedbackText}
                          onChange={(e) => {
                            // Limit input to 500 characters
                            if (e.target.value.length <= 500) {
                              setFeedbackText(e.target.value);
                            }
                          }}
                          placeholder="Share your thoughts, suggestions, or report an issue..."
                          required
                          maxLength={500}
                          className="w-full p-3 border border-gray-300 rounded-xl min-h-[6rem]
                                  focus:ring-2 focus:ring-[#FF5757] focus:border-transparent
                                  bg-white overflow-hidden"
                          style={{ resize: 'none' }}
                        ></textarea>
                        {/* Character counter */}
                        <div className="absolute bottom-2 right-3 text-sm text-gray-400">
                          {feedbackText.length}/500
                        </div>
                        {/* Decorative elements */}
                        <div className="absolute -bottom-1 -right-1 w-12 h-12 bg-gradient-to-r from-[#FF5757] to-[#FFAA00] opacity-20 rounded-full blur-xl"></div>
                      </div>

                      {submitError && (
                        <div className="text-red-500 text-sm">{submitError}</div>
                      )}

                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => setIsModalOpen(false)}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800
                                  transition-colors duration-150 font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-6 py-2 rounded-full font-medium shadow-lg
                                  bg-gradient-to-r from-[#FF5757] to-[#FFAA00] text-white
                                  hover:shadow-xl hover:scale-105 transition-all duration-200
                                  disabled:opacity-50 disabled:cursor-not-allowed
                                  disabled:hover:scale-100 disabled:hover:shadow-lg"
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Feedback;
